import PropTypes from "prop-types";
import * as React from "react";
import { bulkProductAction } from "../../../services/bulkProduct";
import { Alert, Button, Dialog, TextInput } from "../../../themeV2";
import css from "./BulkDeleteDialog.module.css";
import {
  DELETE_CONFIRMATION_PHRASE,
  matchesConfirmationPhrase,
} from "./deleteConfirmation";

const BulkDeleteDialog = React.memo(function BulkDeleteDialog({
  open,
  onClose,
  selectedIds,
  onActionComplete,
}) {
  const [confirmation, setConfirmation] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const count = selectedIds.size;
  const plural = count !== 1 ? "s" : "";
  const isConfirmed = matchesConfirmationPhrase(confirmation);

  const handleOpenChange = React.useCallback(
    (nextOpen) => {
      if (nextOpen || isLoading) return;
      setConfirmation("");
      setError(null);
      onClose();
    },
    [isLoading, onClose],
  );

  const handleDelete = React.useCallback(async () => {
    if (!matchesConfirmationPhrase(confirmation)) return;

    setIsLoading(true);
    setError(null);

    try {
      await bulkProductAction({
        productIds: [...selectedIds],
        action: "archive",
      });
      setConfirmation("");
      onActionComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [confirmation, selectedIds, onActionComplete]);

  return (
    <Dialog.Root alert open={open} onOpenChange={handleOpenChange}>
      <Dialog.Popup size="sm">
        <Dialog.Title className={css.title}>
          Delete {count} Product{plural}?
        </Dialog.Title>

        <Dialog.Description>
          This removes {count} product{plural} from the shop. They will no
          longer appear in the shop builder, on the shop, or in search, and can
          no longer be purchased. Existing orders are not affected.
        </Dialog.Description>

        <p className={css.warning}>
          <strong>This cannot be undone.</strong>
        </p>

        <div className={css.field}>
          <TextInput
            autoFocus
            disabled={isLoading}
            label={`Type "${DELETE_CONFIRMATION_PHRASE}" to confirm`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        {error && (
          <div className={css.error}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        <Dialog.Actions>
          <Button
            variant="text"
            disabled={isLoading}
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            color="error"
            disabled={!isConfirmed}
            loading={isLoading}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

BulkDeleteDialog.displayName = "BulkDeleteDialog";

BulkDeleteDialog.propTypes = {
  onActionComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkDeleteDialog;
